<script setup lang="ts" generic="T extends FetchTicketResponse">
import { computed, ref } from "vue";
import { onKeyStroke } from "@vueuse/core";
import useDateTime from "src/composables/useDateTime";
import { parseUTCDate } from "src/utils/date-utc";
import { FetchTicketResponsePriority, type FetchTicketResponse } from "src/api/models";
import { useNotification } from "src/composables/useNotification";
import { useTicketService } from "src/services/ticket-service";

const props = defineProps<{
  data: FetchTicketResponse;
}>();

const emit = defineEmits<{
  delete: [value: string];
  open: [void];
}>();

const WARNING_THRESHOLD_HOURS = 5;
const focused = ref(false);

const { showInfo } = useNotification();
const { updateTicketPinned } = useTicketService();

const warningIndicator = computed(() => {
  const duration = props.data.expected_duration as unknown as { hours?: number; minutes?: number } | null;
  if (!duration) return false;
  const totalHours = (duration.hours ?? 0) + (duration.minutes ?? 0) / 60;
  return totalHours > WARNING_THRESHOLD_HOURS;
});

const isPinned = computed(() => {
  return !!props.data.pinned;
});

const priorityBorderClass = computed(() => {
  if (isPinned.value) return 'border-l-surface-300 dark:border-l-surface-600';
  switch (props.data.priority) {
    case FetchTicketResponsePriority.high: return 'border-l-red-300 dark:border-l-red-400/60';
    case FetchTicketResponsePriority.medium: return 'border-l-yellow-300 dark:border-l-yellow-400/60';
    case FetchTicketResponsePriority.lows: return 'border-l-blue-300 dark:border-l-blue-400/60';
    default: return 'border-l-surface-300 dark:border-l-surface-600';
  }
});

const unitInfo = computed(() => {
  const ud = props.data.unit;
  if (!ud) return null;

  const parts = [];
  if (ud.fleet_number) parts.push(`#${ud.fleet_number}`);
  if (ud.category_name) parts.push(ud.category_name);
  if (ud.manufacture_name) parts.push(ud.manufacture_name);

  return parts.length > 0 ? parts.join(' / ') : null;
});

const REQUIRED_PLANNING_FIELDS = [
  { key: 'assignee', label: 'Assignee' },
  { key: 'scheduled_date', label: 'Scheduled date' },
  { key: 'expected_duration', label: 'Duration' },
  { key: 'unit', label: 'Unit' },
  { key: 'site', label: 'Site' },
  { key: 'organisation', label: 'Organisation' },
] as const;

const missingFields = computed(() =>
  REQUIRED_PLANNING_FIELDS
    .filter(({ key }) => props.data[key] == null)
    .map(({ label }) => label),
);

const isIncomplete = computed(() => missingFields.value.length > 0);

const { getTime24HourFormat } = useDateTime();

const scheduledTime = computed(() => {
  const sd = props.data.scheduled_date;
  if (!sd) return null;
  const date = parseUTCDate(sd);
  if (isNaN(date.getTime())) return null;
  const hours = date.getHours();
  const minutes = date.getMinutes();
  if (hours === 0 && minutes === 0) return null;
  return getTime24HourFormat(date);
});

onKeyStroke("Backspace", (e) => {
  if (focused.value) emit("delete", props.data.id);
});

function handlePinToggle(event: Event) {
  event.stopPropagation();
  if (props.data.id) {
    const newPinnedState = !props.data.pinned;
    props.data.pinned = newPinnedState;
    updateTicketPinned(props.data.id, newPinnedState);
  }
}
</script>
<template>
  <div
    class="ticket-card relative bg-surface-50 dark:bg-surface-800 border-t border-r border-b border-surface-200 dark:border-t-surface-700 dark:border-r-surface-700 dark:border-b-surface-700 rounded-md border-l-4"
    :class="[
      priorityBorderClass,
      {
        'opacity-60 grayscale-[30%]': isPinned,
        'cursor-pointer': !isPinned,
        'cursor-not-allowed': isPinned,
      }
    ]"
    @click="emit('open')"
    tabindex="0"
    @focus="focused = true"
    @blur="focused = false"
  >
    <!-- Header: ID + Attention | Time -->
    <div class="flex items-center justify-between px-3 pt-2 pb-1">
      <div class="flex items-center gap-1.5">
        <span class="font-mono text-sm font-semibold text-900 dark:text-100">#{{ data.number }}</span>
        <i
          v-if="isIncomplete"
          v-tooltip.top="'Missing: ' + missingFields.join(', ')"
          class="pi pi-exclamation-circle text-orange-500 dark:text-orange-400 flex-shrink-0"
          style="font-size: 0.8rem"
        />
      </div>
      <span v-if="scheduledTime" class="font-mono text-xs font-semibold text-primary-600 dark:text-primary-400">{{ scheduledTime }}</span>
    </div>

    <!-- Title -->
    <div class="px-3 pb-1.5">
      <div class="text-sm text-900 dark:text-100 leading-tight">
        {{ data.title }}
      </div>
    </div>

    <!-- Details -->
    <div class="px-3 pb-2 flex flex-col gap-1">
      <div v-if="data.assignee?.name" class="flex items-center gap-1.5 text-xs text-600 dark:text-400">
        <i class="pi pi-user flex-shrink-0" style="font-size: 0.7rem"></i>
        <span>{{ data.assignee.name }}</span>
      </div>
      <div v-if="data.organisation?.name" class="flex items-center gap-1.5 text-xs text-600 dark:text-400">
        <i class="pi pi-building flex-shrink-0" style="font-size: 0.7rem"></i>
        <span>{{ data.organisation.name }}</span>
      </div>
      <div v-if="data.site?.city" class="flex items-center gap-1.5 text-xs text-600 dark:text-400">
        <i class="pi pi-map-marker flex-shrink-0" style="font-size: 0.7rem"></i>
        <span>{{ data.site.city }}</span>
      </div>
      <div v-if="unitInfo" class="flex items-center gap-1.5 text-xs text-600 dark:text-400">
        <i class="pi pi-truck flex-shrink-0" style="font-size: 0.7rem"></i>
        <span>{{ unitInfo }}</span>
      </div>
    </div>

    <!-- Footer: duration | pin -->
    <div class="flex items-center justify-between px-3 py-1 border-t border-surface-200 dark:border-surface-700 bg-surface-100/50 dark:bg-surface-900/30">
      <span v-if="data.expected_duration"
            class="text-xs font-mono"
            :class="warningIndicator ? 'text-yellow-700 dark:text-yellow-300' : 'text-500 dark:text-400'">
        <i class="pi pi-clock mr-1" style="font-size: 0.65rem"></i>{{ data.expected_duration?.hours }}h{{ data.expected_duration?.minutes ? String(data.expected_duration.minutes).padStart(2, '0') : '' }}
      </span>
      <span v-else></span>
      <button
        type="button"
        class="p-0.5 rounded hover:bg-surface-200 dark:hover:bg-surface-600 transition-colors"
        @click="handlePinToggle"
        :title="isPinned ? 'Pinned -- click to unpin' : 'Click to pin'"
      >
        <i
          class="text-xs"
          :class="[
            isPinned ? 'pi pi-thumbtack text-blue-500' : 'pi pi-lock-open text-400 dark:text-500',
          ]"
        ></i>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ticket-card {
  min-height: 80px;
  transition: all 0.15s ease;

  &.cursor-not-allowed {
    &:hover {
      transform: none !important;
      box-shadow: none !important;
    }
  }

  &.cursor-pointer {
    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      border-top-color: var(--surface-300);
      border-right-color: var(--surface-300);
      border-bottom-color: var(--surface-300);
      transform: translateY(-1px);
    }
  }
}
</style>
